// core/lib/realtimeHandlers.ts — one small function per server -> client
// realtime event type, each independently testable and imported by
// realtime.ts's dispatch table rather than inlined into a giant switch.

import { useNotificationStore } from '@/core/stores/notificationStore';
import { useMessageStore } from '@/core/stores/messageStore';
import { useTypingStore } from '@/core/stores/typingStore';
import { usePresenceStore } from '@/core/stores/presenceStore';
import { useFeedPillStore } from '@/core/stores/feedPillStore';
import { usePostStore } from '@/core/stores/postStore';
import { authService } from '@/core/services/authService';
import { messagesService } from '@/core/services/messagesService';
import type { Notification } from '@/core/models/notification';
import type { Message } from '@/core/models/message';

// W2 (1.21.00 review): a dm.message for a conversation not yet in the store
// (a brand-new DM/group started by the other side) was previously a silent
// no-op — appendMessage only ever patches an EXISTING conversation, and the
// 10s poll fallback is paused while the socket is connected, so the thread
// would simply never appear. Debounced so a burst of messages for the same
// new conversation triggers one refetch, not one per message.
const CONVERSATIONS_REFETCH_DEBOUNCE_MS = 300;
let conversationsRefetchTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleConversationsRefetch(): void {
  if (conversationsRefetchTimer !== null) return;
  conversationsRefetchTimer = setTimeout(() => {
    conversationsRefetchTimer = null;
    void messagesService.refresh().catch(() => {});
  }, CONVERSATIONS_REFETCH_DEBOUNCE_MS);
}

export function handleNotificationNew(payload: Notification): void {
  useNotificationStore.getState().prependNotification(payload);
}

export interface DmMessagePayload {
  conversationId: string;
  message: Message;
  /**
   * 1.24.00 message requests — true when this message lands in a thread the
   * recipient has NOT accepted yet. The frame is still delivered (so the
   * Requests tab updates live) but it must not count as inbox activity.
   */
  pending?: boolean;
}

export function handleDmMessage(payload: DmMessagePayload): void {
  const { conversationId, message } = payload;
  const knownConversation = useMessageStore
    .getState()
    .conversations.some((c) => c.id === conversationId);
  if (!knownConversation) {
    // W2 — a brand-new conversation the store has never seen: appendMessage
    // would silently no-op, so refetch the conversation list instead of
    // dropping the message on the floor.
    scheduleConversationsRefetch();
    return;
  }
  // appendMessage is deduped by id, so this is safe to call even for a message
  // the sender already added optimistically from the awaited send() response.
  useMessageStore.getState().appendMessage(conversationId, message);
  // Don't bump unread for the sender's own echoed message — only messages
  // from the other side should move the unread badge. Whether the thread is
  // currently open or not, ConversationThread/MessagesPage calls markRead on
  // selecting a conversation, so a badge that's briefly stale-then-corrected
  // on next visit is expected, matching the existing poll-driven behavior.
  //
  // 1.24.00 message requests: a message in a thread awaiting MY acceptance is
  // deliberately silent — no push, no notification, and no unread badge until
  // Accept is pressed. Bumping the badge here would put a number on the
  // messages icon for someone the member has not agreed to hear from, which is
  // exactly the thing Requests exists to prevent.
  if (!message.fromMe && !payload.pending) {
    useMessageStore.getState().incrementUnread(conversationId);
  }
  // W10 — the person just finished typing (their message arrived), so clear
  // any lingering "@handle is typing…" for this conversation immediately
  // rather than waiting up to 5s for the auto-clear timer.
  useTypingStore.getState().clearTyping(conversationId);
}

export interface DmReadPayload {
  conversationId: string;
  /** The participant who read — the OTHER side, from this client's point of view. */
  userId: number;
  /** ISO instant they read up to. */
  readAt: string;
}

/**
 * 1.24.00 read receipts. The server only emits this when the reader has read
 * receipts on AND drops it per-socket for a recipient whose own receipts are
 * off, so anything that arrives here is already allowed to be shown.
 */
export function handleDmRead(payload: DmReadPayload): void {
  useMessageStore.getState().setOtherReadAt(payload.conversationId, payload.readAt);
}

export interface ConversationAcceptedPayload {
  conversationId: string;
}

/**
 * 1.24.00 — the other side accepted my message request. Until this frame
 * existed the sender kept reading "Request sent — they'll see it in Requests"
 * under a thread that was already live: the only path that could have
 * corrected it is the 10s conversation poll, and that poll deliberately skips
 * every tick while the socket is connected.
 *
 * The store is patched directly rather than refetched — the change is exactly
 * two booleans, and a refetch would leave the banner up for a whole round trip.
 * An id the store has never seen is ignored (map no-ops), not thrown.
 */
export function handleConversationAccepted(payload: ConversationAcceptedPayload): void {
  useMessageStore.getState().markConversationAccepted(payload.conversationId);
}

export interface ConversationRemovedPayload {
  conversationId: string;
}

/**
 * 1.24.00 — this conversation is gone for BOTH sides (the recipient declined
 * the request). Without it the sender keeps a phantom thread that 404s on every
 * send for the rest of the session. Removing the row is enough to fix the open
 * pane too: MessagesPage derives its active conversation by looking the id up
 * in the visible list, so a removed thread falls back to the list / empty state
 * on the next render instead of leaving a dead pane behind.
 * An unknown id is ignored (filter no-ops), not thrown.
 */
export function handleConversationRemoved(payload: ConversationRemovedPayload): void {
  useMessageStore.getState().removeConversation(payload.conversationId);
}

export interface TypingPayload {
  conversationId: string;
  userId: number;
  handle: string;
}

export function handleTyping(payload: TypingPayload): void {
  useTypingStore.getState().setTyping(payload.conversationId, payload.handle);
}

export interface PresencePayload {
  userId: number;
  online: boolean;
}

export function handlePresence(payload: PresencePayload): void {
  usePresenceStore.getState().setOnline(payload.userId, payload.online);
}

export interface FeedNewPayload {
  postId: string;
  authorId: string;
}

export function handleFeedNew(payload: FeedNewPayload): void {
  // Belt-and-suspenders client-side guard — the server already skips
  // broadcasting to the post's own author, but never trust that alone.
  const currentUser = authService.getCurrentUser();
  if (currentUser && currentUser.id === payload.authorId) return;
  // Already-loaded feed (e.g. the author's own client re-fetched before the
  // broadcast arrived, or a duplicate delivery) shouldn't surface a pill.
  const alreadyLoaded = usePostStore.getState().posts.some((p) => p.id === payload.postId);
  if (alreadyLoaded) return;
  useFeedPillStore.getState().addNewPost(payload.postId);
}
